import React, { useContext, useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ApplicationContext } from '../../context/GlobalState';
import { t, extractLocaleFromPath, localizedPath } from '../../i18n';
import exerciseData from '../../data/exercises/core-exercises-i18n.json';

const INJURY_LINKS = {
  'back-injury': '/injuries/back-injury',
  'knee-injury': '/injuries/knee-injury',
  'ankle-injury': '/injuries/ankle-injury',
};

const EXERCISE_INJURY_MAP = {
  'Bridge': ['back-injury'],
  'Superman': ['back-injury'],
  'Modified Plank': ['back-injury'],
  'Planks': ['back-injury'],
  'Dead Bug': ['back-injury'],
  'Quadruped': ['back-injury'],
  'Modified Plank Variations': ['back-injury'],
  'Pelvic Tilt': ['back-injury'],
  'Knee Extension': ['knee-injury'],
  'Calf Raises': ['ankle-injury'],
};

// Get localized exercise fields
function getLocalizedExercise(ex, locale) {
  if (locale === 'en' || !ex.translations || !ex.translations[locale]) {
    return {
      name: ex.name,
      description: ex.description || '',
      benefits: ex.benefits || [],
      steps: ex.steps || [],
    };
  }
  const trans = ex.translations[locale];
  return {
    name: trans.name || ex.name,
    description: trans.description || ex.description || '',
    benefits: trans.benefits || ex.benefits || [],
    steps: trans.steps || ex.steps || [],
  };
}

// Get localized overview
function getLocalizedOverview(locale) {
  if (locale === 'en' || !exerciseData.overview_translations || !exerciseData.overview_translations[locale]) {
    return exerciseData.overview || '';
  }
  return exerciseData.overview_translations[locale] || exerciseData.overview || '';
}

// ---------------------------------------------------------------------------
// Centered modal component
// ---------------------------------------------------------------------------
function ExerciseModal({ exercise, locale, onClose, modalRef }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    if (!exercise || !modalRef.current) return;
    const modal = modalRef.current;
    const focusable = modal.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    first?.focus();

    const trapFocus = (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last?.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first?.focus(); }
      }
    };
    modal.addEventListener('keydown', trapFocus);
    return () => modal.removeEventListener('keydown', trapFocus);
  }, [exercise, modalRef]);

  if (!exercise) return null;

  const localized = getLocalizedExercise(exercise, locale);

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.55)',
          zIndex: 9998,
          animation: 'fadeInBackdrop 0.2s ease',
        }}
      />
      <div
        ref={modalRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={localized.name}
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 9999,
          width: 'min(600px, 92vw)',
          maxHeight: '85vh',
          overflowY: 'auto',
          background: 'rgba(255,255,255,0.97)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.5)',
          borderRadius: 16,
          boxShadow: '0 24px 80px rgba(0,0,0,0.3)',
          padding: '32px 36px',
          animation: 'slideInModal 0.22s cubic-bezier(0.34,1.56,0.64,1)',
        }}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute',
            top: 14,
            right: 16,
            background: 'transparent',
            border: 'none',
            fontSize: '1.5rem',
            cursor: 'pointer',
            color: 'var(--color-text-secondary)',
            lineHeight: 1,
            padding: '4px 8px',
          }}
        >
          ×
        </button>

        <h2 style={{ color: 'var(--color-primary)', marginBottom: 12, paddingRight: 32 }}>{localized.name}</h2>
        {exercise.images && exercise.images[0] && (
          <img
            src={exercise.images[0]}
            alt={localized.name}
            loading="lazy"
            style={{
              width: '100%',
              maxHeight: 300,
              objectFit: 'cover',
              borderRadius: 12,
              marginBottom: 16,
            }}
          />
        )}
        <p style={{ color: 'var(--color-text)', lineHeight: 1.8, marginBottom: 20 }}>{localized.description}</p>

        {localized.benefits && localized.benefits.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <h4 style={{ color: 'var(--color-primary)', marginBottom: 10 }}>{t('exercise.benefits', locale)}</h4>
            <ul style={{ paddingLeft: 20 }}>
              {localized.benefits.map((b, i) => (
                <li key={i} style={{ marginBottom: 6, color: 'var(--color-text)', lineHeight: 1.6 }}>{b}</li>
              ))}
            </ul>
          </div>
        )}

        {localized.steps && localized.steps.length > 0 && (
          <div>
            <h4 style={{ color: 'var(--color-primary)', marginBottom: 10 }}>{t('exercise.howTo', locale)}</h4>
            <ol style={{ paddingLeft: 20 }}>
              {localized.steps.map((s, i) => (
                <li key={i} style={{ marginBottom: 8, color: 'var(--color-text)', lineHeight: 1.6 }}>{s}</li>
              ))}
            </ol>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeInBackdrop { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideInModal {
          from { opacity: 0; transform: translate(-50%, -50%) scale(0.88); }
          to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        }
      `}</style>
    </>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function CoreExercises() {
  const { state } = useContext(ApplicationContext);
  const location = useLocation();
  const { locale } = extractLocaleFromPath(location.pathname);
  const brand = state.brand;

  const [activeExercise, setActiveExercise] = useState(null);
  const modalRef = useRef(null);
  const openerRef = useRef(null);

  const closeModal = useCallback(() => {
    setActiveExercise(null);
    openerRef.current?.focus();
    openerRef.current = null;
  }, []);

  const exercises = exerciseData.exercises || [];
  const overview = getLocalizedOverview(locale);

  return (
    <div className="core-exercises-page">
      <section className="glass-dark" style={{ padding: '60px 0', textAlign: 'center', borderRadius: 0, marginBottom: 40 }}>
        <div className="container">
          <h1>{t('exercise.pageTitle', locale)}</h1>
          <p style={{ opacity: 0.9 }}>{t('exercise.subtitle', locale, { brandName: brand.name })}</p>
        </div>
      </section>

      <section className="container" style={{ maxWidth: 960, margin: '0 auto', padding: '0 20px 60px' }}>
        <div className="glass-panel" style={{ padding: 28, marginBottom: 40 }}>
          <h2 style={{ marginBottom: 12, color: 'var(--color-primary)' }}>{t('exercise.whyCoreMatters', locale)}</h2>
          <p style={{ color: 'var(--color-text-secondary)', lineHeight: 1.8 }}>{overview}</p>
        </div>

        <p style={{ color: 'var(--color-text-secondary)', marginBottom: 20, fontSize: '0.95rem', textAlign: 'center' }}>
          {t('exercise.clickHint', locale)}
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 24 }}>
          {exercises.map((ex, i) => {
            const relatedInjuries = EXERCISE_INJURY_MAP[ex.name] || [];
            const localized = getLocalizedExercise(ex, locale);

            return (
              <div
                key={i}
                onClick={(e) => { openerRef.current = e.currentTarget; setActiveExercise(ex); }}
                style={{ cursor: 'pointer' }}
              >
                <div
                  className="glass-card"
                  style={{
                    padding: 24,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.18)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'none';
                    e.currentTarget.style.boxShadow = '';
                  }}
                >
                  {ex.images && ex.images[0] && (
                    <img
                      src={ex.images[0]}
                      alt={localized.name}
                      loading="lazy"
                      style={{
                        width: '100%',
                        height: 180,
                        objectFit: 'cover',
                        borderRadius: 8,
                        marginBottom: 12,
                      }}
                    />
                  )}
                  <h3 style={{ marginBottom: 8, color: 'var(--color-primary)' }}>{localized.name}</h3>
                  <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem', lineHeight: 1.6 }}>
                    {localized.description && localized.description.length > 120
                      ? localized.description.slice(0, 120) + '\u2026'
                      : localized.description}
                  </p>
                  {relatedInjuries.length > 0 && (
                    <div style={{ marginTop: 12, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {relatedInjuries.map((inj) => (
                        <Link
                          key={inj}
                          to={localizedPath(INJURY_LINKS[inj], locale)}
                          className="glass-badge"
                          onClick={e => e.stopPropagation()}
                          style={{ fontSize: '0.75rem', textDecoration: 'none' }}
                        >
                          {inj.replace(/-/g, ' ')}
                        </Link>
                      ))}
                    </div>
                  )}
                  <p style={{ marginTop: 10, fontSize: '0.8rem', color: 'var(--color-accent)', opacity: 0.8 }}>
                    {t('exercise.tapForDetails', locale)} →
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="glass-panel" style={{ padding: 28, marginTop: 48, textAlign: 'center' }}>
          <h3 style={{ marginBottom: 12 }}>{t('exercise.needPlan', locale)}</h3>
          <p style={{ color: 'var(--color-text-secondary)', marginBottom: 20 }}>
            {t('exercise.needPlanDesc', locale)}
          </p>
          <Link to={localizedPath('/request-appt', locale)} className="glass-btn glass-btn-primary">
            {t('exercise.scheduleCta', locale)}
          </Link>
        </div>
      </section>

      <ExerciseModal exercise={activeExercise} locale={locale} onClose={closeModal} modalRef={modalRef} />
    </div>
  );
}
